import { Component, Input, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, FormBuilder, FormArray } from '@angular/forms';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { AppService } from 'src/app/modules/section-configuration/services/app.service';
import { App } from '../../models/app.model';
import { AppSelectorComponent } from '../app-selector/app-selector.component';
import type { SectionConfigModel } from '../../../section-configuration/models/section-configuration.model';

@Component({
    selector: 'app-list-selector',
    templateUrl: './app-list-selector.component.html',
    styleUrls: ['./app-list-selector.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppListSelectorComponent {
  @Input() parentFormGroup: FormGroup;
  @Input() appList: string[];
  @Input() sectionConfig?: SectionConfigModel;
  @Input() caption: string;
  @Input() controlName: string;
  @ViewChild('appSelector') appSelector: AppSelectorComponent;

  showAppSelector: boolean = false;
  newApp: App;
  sectionApps: App[] = [];

  constructor(private fb: FormBuilder,
    private readonly appService: AppService) { }

  get canAddNewApp(): boolean {
    return !!this.newApp && !this.appList?.includes(this.newApp.id);
  }

  ngOnInit(): void {
    if (this.appList?.length) {
      const filter = this.sectionConfig?.filter ?? {appIds: this.appList, rules:[]};
      this.appService.getSectionApps(filter).subscribe(response => {
        this.sectionApps = response;
      });
    }
  }

  getAppName(appId: string): string {
    if (this.sectionApps?.length) {
      return this.sectionApps.find(app => app.id === appId)?.name;
    }
  }

  addNewAppToSectionApps(): void {
    if (!this.canAddNewApp) {
      return;
    }

    this.getFormArrayElements().push(this.fb.control(this.newApp.id));

    if (this.appList) {
      this.appList.push(this.newApp.id);
    } else {
      this.appList = [this.newApp.id];
    }

    this.sectionApps.push(this.newApp);

    this.closeAppSelector();
    this.parentFormGroup.markAsTouched();
  }

  private closeAppSelector(): void {
    this.showAppSelector = false;
    this.appSelector?.clearSelection();
  }

  deleteApp(index: number): void {
    const formArray = this.getFormArrayElements();

    this.appList.splice(index, 1);
    formArray.removeAt(index);
  }

  moveAppDown(index: number): void {
    this.moveApps(index, index + 1);
  }

  moveAppUp(index: number): void {
    this.moveApps(index, index - 1);
  }

  drop(event: CdkDragDrop<string[]>) {
    this.moveApps(event.previousIndex, event.currentIndex);
  }

  private moveApps(previousIndex: number, currentIndex: number): void {
    moveItemInArray(this.appList, previousIndex, currentIndex);
    const formArray = this.getFormArrayElements();
    let group = formArray.at(previousIndex);
    formArray.removeAt(previousIndex);
    formArray.insert(currentIndex, group);
  }

  private getFormArrayElements(): FormArray {
    return <FormArray>this.parentFormGroup.controls[this.controlName];
  }
}
